/**
 * «Tall for uka» under the planning week (owner 2026-10-27, prototype foot-e.prototype.html after three reviews):
 * one line per figure, the week's total at the right of the label, a title line with the week's own figures and
 * what is missing, and a row of charts per day behind «Diagram». Replaces the week charts and the bottom row.
 * Everything comes from what the page already reads: the shifts by Plan | Faktisk, the need, the labour cost per
 * day (GET /admin/labour-cost) and the sales forecast.
 */
import type { ApiShift } from '@/api/data';
import type { CostDay, CostParts } from '@/api/labour';
import { T } from '@/i18n';
import type { NeedProfile } from '@/shell/local';
import { toast } from '@/ui';
import { NO_VALUE } from '@/ui/format';
import { useEffect, useRef, useState } from 'react';
import { ChartsButton, FoldButton, MissLine, useNotUsed, useStored } from './FiguresBits';
import css from './WeekFigures.module.css';
import { coverState, hourCover, hourRange } from './chartFigures';
import { type DayNumbers, dayNumbers, h1, hoursOff, kr, missing, p1, pt, sumDays, verdict } from './figures';
import { type SalesDay, hourGaps, needHours } from './staffing';
import type { TMode } from './timeMode';

type Chart = 'cov' | 'tim' | 'kinds';

const hh = (h: number) => String(h).padStart(2, '0');

/** Sales as typed: «27 000», «27000,50» and «27000.5» are kroner, a separator before 1 or 2 digits at the end is
 *  the decimal mark, any other a thousands mark; 0 is a closed day; under 1 000 is refused, as the app always did. */
export function readSales(raw: string): { v: number | null } | { err: string } {
  let t = raw.replace(/\s|kr/gi, '');
  if (t === '') return { v: null };
  const m = t.match(/^(.*)[.,](\d{1,2})$/);
  t = m ? `${(m[1] ?? '').replace(/[.,]/g, '')}.${m[2]}` : t.replace(/[.,]/g, '');
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return { err: T('Skriv et kronebeløp, f.eks. 32 000.') };
  if (n > 0 && n < 1000) return { err: T('Skriv hele kronebeløpet, f.eks. 32 000.') };
  return { v: Math.round(n) };
}

/** Grunnlønn, tillegg (every supplement) and sosiale kostnader (feriepenger, pensjon, AGA) of a side's cost. */
const kindsOf = (p: CostParts | undefined) =>
  p
    ? [
        { k: 'k1', n: T('Grunnlønn'), v: p.base },
        { k: 'k2', n: T('Tillegg'), v: p.supplements.reduce((a, s) => a + s.cost, 0) },
        { k: 'k4', n: T('Sosiale'), v: p.on_top },
      ]
    : null;

export function WeekFigures({
  unknown = false,
  days,
  today,
  tmode,
  need,
  target,
  targetSet,
  shifts,
  planHours,
  openHours,
  weekCol,
  cost,
  sales,
  lastWeekPct,
  money,
  tenantId,
  style,
  dayClass,
  onSales,
  onOpenDay,
  onHugin,
}: {
  /** the shifts have not answered (pending or failed): no figure, gap or amber from an empty week (#272 A-02) */
  unknown?: boolean;
  days: string[];
  today: string;
  tmode: TMode | undefined;
  need: NeedProfile;
  target: number;
  /** whether the company set its wage target, or the page uses the 30 % default */
  targetSet: boolean;
  /** the shifts as Plan | Faktisk shows them */
  shifts: ApiShift[];
  /** the planned hours on a day, for «plan X» under a finished day in Faktisk */
  planHours: (d: string) => number;
  openHours: number;
  /** the grid has its «Uka» column: the week's totals go there, else they sit at the right of the labels */
  weekCol: boolean;
  cost: Map<string, CostDay>;
  sales: Record<string, SalesDay>;
  lastWeekPct: number | null;
  /** may this reader see kroner (vaktplan.kost) */
  money: boolean;
  tenantId: number | undefined;
  style: React.CSSProperties;
  dayClass: (d: string) => string | undefined;
  onSales: (day: string, value: number | null, forecast: boolean) => void;
  onOpenDay: (d: string) => void;
  onHugin: (() => void) | null;
}) {
  const [open, setOpen] = useStored('vaktplan.figures.open', true);
  const [charts, setCharts] = useStored('vaktplan.figures.charts', false);
  const [pick, setPick] = useStored<Chart>('vaktplan.figures.chart', 'cov');
  const [notUsed, setNotUsed] = useNotUsed(tenantId);
  const [bad, setBad] = useState<Record<string, { err: string; text: string }>>({});
  // the sticky foot never hides what the keyboard moves to (SH-E1): the scroller keeps the foot's height clear
  // at its bottom, and a foot taller than 40 % of a short scroller stops sticking and scrolls with the rows
  const foot = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: folding swaps the element the ref holds
  useEffect(() => {
    const el = foot.current;
    const sc = el?.closest<HTMLElement>('.gscroll');
    if (!el || !sc) return;
    const fit = () => {
      const loose = el.offsetHeight > sc.clientHeight * 0.4;
      el.classList.toggle(`${css.loose}`, loose);
      sc.style.scrollPaddingBottom = loose ? '' : `${el.offsetHeight + 8}px`;
    };
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    ro.observe(sc);
    return () => {
      ro.disconnect();
      sc.style.scrollPaddingBottom = '';
    };
  }, [open]);
  const [hov, setHov] = useState<{ x: number; y: number; lines: string[] } | null>(null);

  const fakt = tmode === 'fakt';
  const state = (d: string) => (!fakt ? 'plan' : d < today ? 'done' : d === today ? 'now' : 'later');
  const counted = (d: string) => state(d) === 'plan' || state(d) === 'done';
  const needSet = days.some((d) => needHours(need, d) != null);
  const needOn = needSet && !notUsed.includes('need');
  const salesUsed = money && !notUsed.includes('sales');
  const target1 = target + 1;

  // each day as the rows show it: the shared count, and the hours under the need
  const day = (d: string) => {
    const x = dayNumbers(d, { today, tmode, need, needOn, shifts, cost, sales });
    return { ...x, gaps: x.nh != null ? hourGaps(need, d, shifts) : [] };
  };
  const all = days.map(day);
  const anySales = all.some((x) => x.f.sales != null);
  const unpriced = all.reduce((a, x) => a + (counted(x.d) ? x.unpriced : 0), 0);
  const showMoney = salesUsed && anySales;
  // wage cost needs no sales: a firm without a till still plans against what the week costs (review 2026-10-27)
  const costOn = money;
  const over = (p: number | null) => targetSet && p != null && p > target1;

  // the week: the days counted, the share over the days with sales only, a closed day (0) left out
  const w = sumDays(all.filter((x) => counted(x.d)));
  const wPct = w.pct;
  const salesDays = w.sd;
  const tag = fakt ? T('hittil') : w.e ? T('prognose') : '';

  // what is missing, and where it is filled in; × says the company does not use it
  const miss = missing({ needSet, money, anySales, targetSet, unpriced, notUsed });

  const [h0, h1x] = hourRange(need, days, shifts);
  const cover = all
    .filter((x) => needOn && counted(x.d))
    .flatMap((x) => hourCover(need, x.d, shifts, h0, h1x))
    .filter((z) => z.need > 0);
  const covered = cover.filter((z) => z.on + 1e-6 >= z.need).length;
  const cellCls = (d: string, extra = '') => [dayClass(d) ?? '', extra].filter(Boolean).join(' ');
  /** a row: the label with the week's figure at its right, then the days; Faktisk says «Pågår» or «Ikke ennå» once */
  const row = (
    key: string,
    label: React.ReactNode,
    total: React.ReactNode,
    cell: (x: DayNumbers & { gaps: ReturnType<typeof hourGaps> }) => React.ReactNode,
    first = false,
    withTag = false,
  ) => (
    <div className={`grow ${css.row}`} style={style} key={key}>
      <div className={css.lab}>
        <span className={css.name}>{label}</span>
        {!weekCol && (
          <span className={css.tot} title={tag ? T('Uka, {tag}', { tag }) : undefined}>
            {fakt && !w.k ? <span className={css.sub}>{T('Ingen dager ferdig')}</span> : total}
          </span>
        )}
      </div>
      {all.map((x) =>
        counted(x.d) ? (
          <div key={x.d} className={cellCls(x.d)}>
            {cell(x)}
          </div>
        ) : (
          <div key={x.d} className={cellCls(x.d, css.idle)}>
            {first && <span className={css.sub}>{state(x.d) === 'now' ? T('Pågår') : T('Ikke ennå')}</span>}
          </div>
        ),
      )}
      {weekCol && (
        <div className={css.wk}>
          {fakt && !w.k ? (
            <span className={css.sub}>{T('Ingen dager ferdig')}</span>
          ) : (
            <>
              {tag && withTag && <small className={css.sub}>{tag}</small>}
              <span className={css.tot}>{total}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
  const star = unpriced ? '*' : '';
  const pctText = (p: number) => (
    <span className={!unpriced && over(p) ? css.warn : undefined}>
      {p1(p)}
      {star}
    </span>
  );

  // two weeks are a period, not a week (#272 A-23)
  const toggle = (
    <FoldButton
      open={open}
      onClick={() => setOpen(!open)}
      label={days.length > 7 ? T('Tall for perioden') : T('Tall for uka')}
    />
  );

  if (unknown) {
    return (
      <div ref={foot} className={css.wrap} data-testid="week-figures">
        <div className={`grow ${css.head}`} style={style}>
          <div className={css.lab}>{toggle}</div>
          <div className={css.wl}>
            <span>
              {T('Timer')} <b>{NO_VALUE}</b>
            </span>
            <span>
              {T('Ledige')} <b>{NO_VALUE}</b>
            </span>
          </div>
        </div>
      </div>
    );
  }

  // «Mangler»: the same line open and folded
  const missLine = (
    <MissLine
      miss={miss}
      onHide={(k) => setNotUsed([...notUsed, k])}
      onSales={() => {
        setOpen(true);
        setTimeout(() => document.querySelector<HTMLInputElement>('[data-wf-sales] input')?.focus());
      }}
    />
  );

  if (!open) {
    // folded: the period's figures as label, value and qualifier (#280 Vaktplan C), so the clock hours with enough
    // people, the person-hours and the wage share never read as one number; the counting is the open rows' own
    const lead = tag ? `${tag.charAt(0).toUpperCase()}${tag.slice(1)}` : '';
    return (
      <div ref={foot} className={css.wrap} data-testid="week-figures">
        <div className={`grow ${css.head}`} style={style}>
          <div className={css.lab}>{toggle}</div>
          <div className={`${css.wl} ${css.stats}`}>
            {needOn && cover.length > 0 && (
              <span className={css.stat} data-testid="figures-covered">
                <span className={css.statLab}>{T('Dekket')}</span>{' '}
                <b className={covered < cover.length ? css.warn : undefined}>
                  {T('{a} av {b} timer', { a: covered, b: cover.length })}
                </b>{' '}
                <small className={css.statSub}>{T('Klokketimer med nok folk')}</small>
              </span>
            )}
            <span className={css.stat} data-testid="figures-hours">
              <span className={css.statLab}>{T('Timer')}</span>{' '}
              <b className={needOn && hoursOff(w.h, w.n) ? css.warn : undefined}>
                {needOn ? T('{used} av {budget} t', { used: h1(w.h), budget: h1(w.n) }) : `${h1(w.h)} t`}
              </b>{' '}
              <small className={css.statSub}>{fakt ? T('Persontimer, hittil') : T('Persontimer')}</small>
            </span>
            {showMoney && wPct != null && (
              <span className={css.stat} data-testid="figures-pct">
                <span className={css.statLab}>{T('Lønn %')}</span> <b>{pctText(wPct)}</b>{' '}
                <small className={css.statSub}>
                  {[lead, T('{a} av {b} dager', { a: salesDays, b: w.k })].filter(Boolean).join(', ')}
                </small>
              </span>
            )}
            {missLine}
          </div>
        </div>
      </div>
    );
  }

  const hours = (x: DayNumbers & { gaps: ReturnType<typeof hourGaps> }) => {
    const short = hoursOff(x.hours, x.nh) || x.gaps.length > 0;
    // whole hours of the gaps, «07-13»; the minutes are in the title
    const gapLine = x.gaps.map((g) => `${g.from.slice(0, 2)}-${g.to.slice(0, 2)}`).join(', ');
    const title = `${T('Åpne dagen')}${x.gaps.length ? `. ${T('Under behovet kl. {list}', { list: x.gaps.map((g) => `${g.from}-${g.to}`).join(', ') })}` : ''}`;
    return (
      <button
        type="button"
        className={css.daybtn}
        title={title}
        onClick={() => onOpenDay(x.d)}
        data-testid="cover-box"
      >
        <span className={short ? css.warn : undefined}>
          {h1(x.hours)}
          {x.nh != null && ` ${T('av')} ${h1(x.nh)}`} t
        </span>
        {gapLine ? (
          <span className={`${css.sub} ${css.warn}`}>{T('kl. {time}', { time: gapLine })}</span>
        ) : x.nh != null && x.hours - 0.05 > x.nh ? (
          <span className={css.sub}>+{h1(x.hours - x.nh)} t</span>
        ) : null}
        {state(x.d) === 'done' && <span className={css.sub}>plan {h1(planHours(x.d))} t</span>}
      </button>
    );
  };
  const salesCell = (x: DayNumbers & { gaps: ReturnType<typeof hourGaps> }) => {
    const own = x.past ? (x.s?.actual ?? null) : (x.s?.forecast ?? null);
    const b = bad[x.d];
    const reset = !x.past && x.s?.forecast != null && x.s?.estimate != null;
    return (
      <span className={css.sales}>
        <input
          className={`inp ${b ? css.bad : ''} ${reset ? css.hasReset : ''}`}
          inputMode="decimal"
          aria-invalid={!!b}
          title={
            b?.err ??
            (x.est ? T('Snitt av de siste fire like ukedagene. Skriv inn for å bruke ditt eget tall.') : '')
          }
          aria-label={`${x.past ? T('Omsetning uten mva') : T('Forventet omsetning uten mva')} ${x.d}`}
          placeholder={
            x.past
              ? T('Uten mva')
              : x.s?.estimate != null
                ? `≈ ${kr(x.s.estimate)}`
                : T('Forventet, uten mva')
          }
          key={`${x.d}-${own ?? ''}-${b?.text ?? ''}`}
          defaultValue={b ? b.text : own != null ? kr(own) : ''}
          onBlur={(e) => {
            const r = readSales(e.target.value);
            if ('err' in r) {
              setBad({ ...bad, [x.d]: { err: r.err, text: e.target.value } });
              toast(r.err);
              return;
            }
            const { [x.d]: _, ...rest } = bad;
            setBad(rest);
            if (r.v === own) return;
            if (r.v === 0) toast(T('Lagret som stengt dag'));
            onSales(x.d, r.v, !x.past);
          }}
        />
        {reset && (
          <button
            type="button"
            className={css.reset}
            title={T('Tilbake til estimatet ≈ {kr}', { kr: kr(x.s?.estimate ?? 0) })}
            aria-label={T('Tilbake til estimatet')}
            onClick={() => onSales(x.d, null, true)}
          >
            ↺
          </button>
        )}
      </span>
    );
  };
  const pctCell = (x: DayNumbers & { gaps: ReturnType<typeof hourGaps> }) => {
    if (x.f.sales === 0) return <span className={css.sub}>{T('stengt')}</span>;
    if (x.f.share == null) return <span className={css.sub}>{NO_VALUE}</span>;
    const tip = [
      x.est ? T('Regnet med estimatet.') : '',
      x.unpriced
        ? T('For lav: vakter uten timelønn.')
        : targetSet && x.f.sales
          ? verdict(x.f.cost, x.f.sales, target)
          : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <span className={css.line} title={tip}>
        <span className={!x.unpriced && over(x.f.share) ? css.warn : undefined}>
          {x.est ? '≈ ' : ''}
          {p1(x.f.share)}
          {x.unpriced ? '*' : ''}
        </span>
        {state(x.d) === 'done' && x.f.planShare != null && (
          <span className={css.sub}>plan {p1(x.f.planShare)}</span>
        )}
      </span>
    );
  };

  const hourLines = (
    x: DayNumbers & { gaps: ReturnType<typeof hourGaps> },
    z: { h: number; need: number; on: number; low: number },
  ) => [
    `${x.d.slice(8)}.${x.d.slice(5, 7)}. kl. ${hh(z.h)}-${hh(z.h + 1)}`,
    ...(needOn ? [`k-need~${T('Behov')}: ${h1(z.need)}`] : []),
    `k-line~${T('På jobb')}: ${h1(z.on)}`,
    ...(needOn
      ? [
          `st-${coverState(z)}~${z.on + 1e-6 < z.need ? T('Mangler {n}', { n: h1(z.need - z.on) }) : z.on - z.need >= 1 ? T('{n} for mange', { n: h1(z.on - z.need) }) : T('Nok')}`,
        ]
      : []),
  ];
  const onHover = (e: React.MouseEvent) => {
    const t = (e.target as Element).closest('[data-hv]');
    if (!t) return setHov(null);
    setHov({ x: e.clientX, y: e.clientY, lines: (t.getAttribute('data-hv') ?? '').split('|') });
  };
  const chartCell = (x: DayNumbers & { gaps: ReturnType<typeof hourGaps> }) => {
    if (chart === 'cov' || chart === 'tim') {
      const zs = hourCover(need, x.d, shifts, h0, h1x);
      const n = zs.length || 1;
      const W = 100;
      const H = chart === 'tim' ? 64 : 28;
      const mx = Math.max(1, ...zs.map((z) => Math.max(z.need, z.on))) * 1.1;
      const X = (k: number) => (k / n) * W;
      const Y = (v: number) => H - (v / mx) * (H - 2);
      const step = (get: (z: (typeof zs)[number]) => number) =>
        zs.map((z, k) => `${k ? 'L' : 'M'}${X(k)} ${Y(get(z))}L${X(k + 1)} ${Y(get(z))}`).join('');
      return (
        <>
          <svg
            className={chart === 'tim' ? css.tall : css.low}
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={chart === 'cov' ? T('Dekning per time') : T('Bemanning per time')}
          >
            {chart === 'cov' ? (
              zs.map((z, k) => (
                <rect
                  key={z.h}
                  className={css[`st_${coverState(z)}`]}
                  x={X(k) + 0.3}
                  y={0}
                  width={W / n - 0.6}
                  height={H}
                  data-hv={hourLines(x, z).join('|')}
                />
              ))
            ) : (
              <>
                {needOn && <path className={css.need} d={`${step((z) => z.need)}L${W} ${H}L0 ${H}Z`} />}
                <path className={css.on} d={step((z) => z.on)} />
                {zs.map((z, k) => (
                  <rect
                    key={z.h}
                    className={css.hit}
                    x={X(k)}
                    y={0}
                    width={W / n}
                    height={H}
                    data-hv={hourLines(x, z).join('|')}
                  />
                ))}
              </>
            )}
          </svg>
          <div className={css.axis} aria-hidden="true">
            <span>{hh(h0)}</span>
            <span>{hh(Math.round((h0 + h1x) / 2))}</span>
            <span>{hh(h1x)}</span>
          </div>
        </>
      );
    }
    const ks = kindsOf(x.parts);
    if (!ks) return <span className={css.sub}>{NO_VALUE}</span>;
    const sum = ks.reduce((a, k) => a + k.v, 0) || 1;
    return (
      <div className={css.kinds}>
        <div className={css.bar}>
          {ks.map((k) => (
            <i key={k.k} className={css[k.k]} style={{ flex: k.v / sum }} />
          ))}
        </div>
        {ks.map((k) => (
          <span key={k.k} title={k.n}>
            <i className={css[k.k]} />
            <b>{kr(k.v)}</b>
          </span>
        ))}
      </div>
    );
  };
  const opts: [Chart, string][] = [
    needOn ? ['cov', T('Dekning')] : ['tim', T('Bemanning')],
    ...(costOn ? ([['kinds', T('Lønnskost')]] as [Chart, string][]) : []),
  ];
  const chart: Chart = opts.some(([k]) => k === pick) ? pick : (opts[0]?.[0] ?? 'tim');
  const chartKey =
    chart === 'kinds' ? (
      <span className={css.key}>
        <span title={T('Grunnlønn')}>
          <i className={css.k1} />
          {T('Grunnlønn')}
        </span>
        <span title={T('Kveld, natt og helg')}>
          <i className={css.k2} />
          {T('Tillegg')}
        </span>
        <span title={T('Feriepenger, pensjon og arbeidsgiveravgift')}>
          <i className={css.k4} />
          {T('Sosiale')}
        </span>
      </span>
    ) : null;
  // the week's own figure in the chart row: the share bar of the week, or the week's parts
  const wkParts = all
    .filter((x) => counted(x.d))
    .map((x) => kindsOf(x.parts))
    .filter((k): k is NonNullable<ReturnType<typeof kindsOf>> => !!k)
    .reduce((a, ks) => a.map((v, i) => v + (ks[i]?.v ?? 0)), [0, 0, 0]);
  const wkSum = wkParts.reduce((a, v) => a + v, 0);
  const weekChart =
    chart === 'kinds' && wkSum > 0 ? (
      <div className={css.kinds}>
        <div className={css.bar}>
          {(['k1', 'k2', 'k4'] as const).map((k, i) => (
            <i key={k} className={css[k]} style={{ flex: (wkParts[i] ?? 0) / wkSum }} />
          ))}
        </div>
        <b>{kr(wkSum)} kr</b>
      </div>
    ) : null;
  const perHour = chart === 'cov' || chart === 'tim';

  return (
    <div ref={foot} className={css.wrap} data-testid="week-figures">
      <div className={`grow ${css.head}`} style={style}>
        <div className={css.lab}>{toggle}</div>
        <div className={css.wl}>
          {missLine}
          {showMoney && lastWeekPct != null && (
            <span>
              {T('Forrige uke, faktisk')} <b>{p1(lastWeekPct)}</b>
            </span>
          )}
          <span>
            {T('Ledige')} <b>{h1(openHours)} t</b>
          </span>
          <span className={css.sp} />
          <ChartsButton open={charts} onClick={() => setCharts(!charts)} />
          {onHugin && showMoney && !fakt && !unpriced && over(wPct) && (
            <button type="button" className={css.hug} onClick={onHugin}>
              ✦ {T('Nå {p} med Hugin', { p: pt(target) })}
            </button>
          )}
        </div>
      </div>
      {row(
        'hours',
        T('Timer'),
        needOn ? (
          <span className={hoursOff(w.h, w.n) ? css.warn : undefined}>
            {h1(w.h)} av {h1(w.n)} t
          </span>
        ) : (
          `${h1(w.h)} t`
        ),
        hours,
        true,
        fakt,
      )}
      {salesUsed && (
        <div id="wf-oms" data-wf-sales="">
          {row(
            'sales',
            <>
              {T('Omsetning')}{' '}
              <span
                className={css.info}
                title={T(
                  'Uten mva, som kassen viser. Fram i tid: det dere venter. ≈ er snittet av de siste fire like ukedagene.',
                )}
              >
                i
              </span>
            </>,
            anySales ? <>{kr(w.s)} kr</> : <span className={css.sub}>{NO_VALUE}</span>,
            salesCell,
            false,
            true,
          )}
        </div>
      )}
      {costOn &&
        row(
          'cost',
          T('Lønnskost'),
          `${kr(w.c)} kr${star}`,
          (x) => (
            <span title={x.unpriced ? T('For lav: vakter uten timelønn') : undefined}>
              {kr(x.f.cost)} kr{x.unpriced ? '*' : ''}
            </span>
          ),
          false,
          fakt,
        )}
      {salesUsed &&
        row(
          'pct',
          <>
            {T('Lønn %')} {targetSet && <span className={css.sub}>{T('mål {p}', { p: pt(target) })}</span>}{' '}
            <span
              className={css.info}
              title={T(
                'Lønn med tillegg, feriepenger, pensjon og arbeidsgiveravgift, delt på omsetningen uten mva. Oransje fra ett poeng over målet.',
              )}
            >
              i
            </span>
          </>,
          wPct == null ? (
            <span className={css.sub}>{NO_VALUE}</span>
          ) : (
            <span
              className={css.line}
              title={[
                targetSet && !unpriced ? verdict(w.pc, w.s, target) : '',
                salesDays < w.k
                  ? T('Regnet på {a} av {b} dager, dagene uten omsetning er ikke med', {
                      a: salesDays,
                      b: w.k,
                    })
                  : '',
                tag,
              ]
                .filter(Boolean)
                .join('. ')}
            >
              {pctText(wPct)}
              {/* the verdict and the day count where there is room, the «Uka» column; beside the label, in the title */}
              {weekCol && targetSet && !unpriced && (
                <span className={css.sub}>{verdict(w.pc, w.s, target)}</span>
              )}
              {weekCol && salesDays < w.k && (
                <span className={css.sub}>{T('{a} av {b} dager', { a: salesDays, b: w.k })}</span>
              )}
            </span>
          ),
          pctCell,
          false,
          true,
        )}
      {charts && (
        <div
          className={`grow ${css.charts} ${perHour ? css.perHour : ''}`}
          style={style}
          onMouseMove={onHover}
          onMouseLeave={() => setHov(null)}
        >
          <div className={css.lab}>
            <select
              className="inp"
              aria-label={T('Diagram')}
              value={chart}
              onChange={(e) => setPick(e.target.value as Chart)}
            >
              {opts.map(([k, n]) => (
                <option key={k} value={k}>
                  {n}
                </option>
              ))}
            </select>
            {!weekCol && chartKey}
          </div>
          {all.map((x) => (
            <div key={x.d} className={cellCls(x.d, counted(x.d) ? '' : css.idle)}>
              {counted(x.d) ? chartCell(x) : null}
            </div>
          ))}
          {weekCol && (
            <div className={css.wkChart}>
              {perHour ? (
                <span className={css.sub}>{T('{a} til {b}', { a: hh(h0), b: hh(h1x) })}</span>
              ) : (
                <>
                  {weekChart}
                  {chartKey}
                </>
              )}
            </div>
          )}
        </div>
      )}
      {hov && (
        <div
          className={css.hov}
          role="tooltip"
          style={{ left: Math.min(window.innerWidth - 220, hov.x + 14), top: Math.max(8, hov.y - 96) }}
        >
          <b>{hov.lines[0]}</b>
          {hov.lines.slice(1).map((l) => {
            const [cls, t] = l.includes('~') ? l.split('~') : ['', l];
            return (
              <span key={l} className={css.hl}>
                <i className={cls ? (css[cls.replace('-', '_')] ?? '') : ''} />
                {t}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
